import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import APLPresenceWeeksView from "./APLPresenceWeeksView";
import { GetAttendanceStatuses } from "../../actions/teacherQueries";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import { Col, Row, Card, Tooltip, Button, Input } from "reactstrap";
import APLPresenceColorInfo from "./APLPresenceColorInfo";
import { FormattedMessage, useIntl } from "react-intl";
import FoodCompensationInformationForStudentModal from "../Modal/FoodCompensationInformationForStudentModal";
import { getWeek, getYear, format, parseISO } from "date-fns";
import { dateIsSameDay } from "../../Utilities/dateUtils";
import DatePicker from "react-datepicker";
import { sv } from "date-fns/locale";
import CheckInMapForAll from "./CheckInMapForAll";
import useAuthenticatedImage from "../../hooks/useAuthenticatedImage";

const TODAY_STATUS = {
  present: { dotModifier: "present", messageId: "Present" },
  absent: { dotModifier: "absent", messageId: "Absent" },
  noCheckIn: { dotModifier: "empty", messageId: "NoCheckIn" },
};

const RECORD_STATUS = {
  WasPresent: "present",
  HadAbsence: "absent",
};

function getInitials(name) {
  if (!name) return "";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function StudentAvatar({ studentId, avatar, name }) {
  const avatarUrl = useAuthenticatedImage(
    avatar ? `/User/ProfilePicture/${studentId}` : null,
  );
  return (
    <div className="apl-presence__avatar">
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="apl-presence__avatar-img" />
      ) : (
        <span>{getInitials(name)}</span>
      )}
    </div>
  );
}

function Donut({ id, value, total, color, labelId }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const dash = total > 0 ? (value / total) * circumference : 0;
  return (
    <div id={id} className="apl-presence__donut">
      <svg viewBox="0 0 100 100" className="apl-presence__donut-svg">
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#e7e7e7"
          strokeWidth="10"
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
        />
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize="22"
          fontWeight="700"
          fill="#1f2937"
        >
          {value}
        </text>
      </svg>
      <div className="apl-presence__donut-label">
        <FormattedMessage id={labelId} />
      </div>
    </div>
  );
}

export default function APLPresence({
  students,
  objId,
  level,
  activePeriodId,
}) {
  const attendanceByWeek = GetAttendanceStatuses(level, activePeriodId, objId);
  const [presentTooltipIsOpen, setPresentTooltipIsOpen] = useState(false);
  const [absentTooltipIsOpen, setAbsentTooltipIsOpen] = useState(false);
  const [noCheckInTooltipIsOpen, setNoCheckInTooltipIsOpen] = useState(false);
  const [studentForFoodCompensation, setStudentForFoodCompensation] =
    useState(null);
  const [selectedDateForPosition, setSelectedDateForPosition] = useState(null);
  const [markersWithPosition, setMarkersWithPosition] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [pendingDate, setPendingDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const intl = useIntl();
  const lastEmptyToastRef = useRef(null);

  const todayStatusByStudentId = useMemo(() => {
    const map = new Map();
    if (!attendanceByWeek.data) {
      return map;
    }
    const today = new Date();

    for (const week of attendanceByWeek.data.weeks) {
      for (const record of week.attendanceForStudents) {
        if (!dateIsSameDay(record.day, today)) continue;
        map.set(record.studentId, RECORD_STATUS[record.status] ?? "noCheckIn");
      }
    }
    return map;
  }, [attendanceByWeek.data]);

  const studentsByStatus = useMemo(() => {
    const grouped = { present: [], absent: [], noCheckIn: [] };

    students.forEach((stud) => {
      const status = todayStatusByStudentId.get(stud.studentId) || "noCheckIn";
      grouped[status].push({
        studentId: stud.studentId,
        name: stud.student.name,
      });
    });
    return grouped;
  }, [students, todayStatusByStudentId]);

  const notCheckedInRightNow = useMemo(
    () => new Set(studentsByStatus.noCheckIn.map((s) => s.studentId)),
    [studentsByStatus],
  );

  useEffect(() => {
    if (!attendanceByWeek.data || selectedDateForPosition == null) {
      return;
    }
    const selectedWeek = getWeek(selectedDateForPosition, {
      weekStartsOn: 1,
      firstWeekContainsDate: 4,
      locale: sv,
    });
    const selectedYear = getYear(selectedDateForPosition);
    const attendancesForTheWeek = attendanceByWeek.data.weeks.find(
      (w) => w.weekNumber === selectedWeek && w.year === selectedYear,
    );
    const attendancesForTheDay =
      attendancesForTheWeek?.attendanceForStudents.filter(
        (studAtt) =>
          new Date(studAtt.day).getTime() ===
            selectedDateForPosition.getTime() &&
          studAtt.attendanceThisDay != null,
      ) ?? [];
    const tempMarkerpoints = [];
    attendancesForTheDay.forEach((item) => {
      const points = item.attendanceThisDay?.points ?? [];
      const filteredPoints = points.filter(
        (p) => p.latitude != null && p.longitude != null,
      );
      if (filteredPoints.length === 0) return;
      const lastPoint = filteredPoints.reduce((latest, current) =>
        new Date(current.time) > new Date(latest.time) ? current : latest,
      );
      tempMarkerpoints.push({
        studentName: item.attendanceThisDay.studentName,
        studentId: item.attendanceThisDay.studentId,
        position: {
          lat: lastPoint.latitude,
          lng: lastPoint.longitude,
        },
        time: lastPoint.time,
      });
    });
    if (tempMarkerpoints.length > 0) {
      setMarkersWithPosition(tempMarkerpoints);
    } else {
      setMarkersWithPosition(null);
      if (lastEmptyToastRef.current !== selectedDateForPosition) {
        toast.info(<FormattedMessage id="NoLocationsFoundForDate" />);
        lastEmptyToastRef.current = selectedDateForPosition;
      }
    }
  }, [attendanceByWeek.data, selectedDateForPosition]);

  const togglePresentTooltip = () =>
    setPresentTooltipIsOpen(!presentTooltipIsOpen);
  const toggleAbsentTooltip = () =>
    setAbsentTooltipIsOpen(!absentTooltipIsOpen);
  const toggleNoCheckInTooltip = () =>
    setNoCheckInTooltipIsOpen(!noCheckInTooltipIsOpen);

  const handleJumpToStudent = (studentId) => {
    setActiveTab("all");
    setSearchQuery("");
    setPresentTooltipIsOpen(false);
    setAbsentTooltipIsOpen(false);
    setNoCheckInTooltipIsOpen(false);
    setTimeout(() => {
      const el = document.getElementById(`apl-student-${studentId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  };

  const renderStudentLink = (stud) => (
    <Row key={stud.studentId}>
      <Col>
        <a
          href={`#apl-student-${stud.studentId}`}
          className="apl-presence__students-tooltip-link"
          onClick={(e) => {
            e.preventDefault();
            handleJumpToStudent(stud.studentId);
          }}
        >
          {stud.name}
        </a>
      </Col>
    </Row>
  );

  if (attendanceByWeek.isPending) {
    return <Pacmanloader />;
  }
  if (!attendanceByWeek.data) {
    return null;
  }

  const search = searchQuery.trim().toLowerCase();

  const filteredStudents = students.filter((student) => {
    const matchesTab =
      activeTab !== "alerts" || notCheckedInRightNow.has(student.studentId);
    const matchesSearch =
      !search || student.student.name.toLowerCase().includes(search);
    return matchesTab && matchesSearch;
  });

  return (
    <div>
      <div className="apl-presence__summary">
        <div className="apl-presence__donut-card">
          <div className="apl-presence__donut-card-title">
            <FormattedMessage id="TodayStatus" />
          </div>
          <Donut
            id="Tooltip-Present"
            value={studentsByStatus.present.length}
            total={students.length}
            color="#4ee474"
            labelId="Present"
          />
          <div className="apl-presence__donut-divider" />
          <Donut
            id="Tooltip-Absent"
            value={studentsByStatus.absent.length}
            total={students.length}
            color="#ff5733"
            labelId="Absent"
          />
          <div className="apl-presence__donut-divider" />
          <Donut
            id="Tooltip-NoCheckIn"
            value={studentsByStatus.noCheckIn.length}
            total={students.length}
            color="#9ca3af"
            labelId="NoCheckIn"
          />
          <Tooltip
            placement="bottom"
            isOpen={presentTooltipIsOpen}
            target={"Tooltip-Present"}
            toggle={togglePresentTooltip}
            fade={false}
            autohide={false}
            className="apl-presence__students-tooltip"
          >
            {studentsByStatus.present.map(renderStudentLink)}
          </Tooltip>
          <Tooltip
            placement="bottom"
            isOpen={absentTooltipIsOpen}
            target={"Tooltip-Absent"}
            toggle={toggleAbsentTooltip}
            fade={false}
            autohide={false}
            className="apl-presence__students-tooltip"
          >
            {studentsByStatus.absent.map(renderStudentLink)}
          </Tooltip>
          <Tooltip
            placement="bottom"
            isOpen={noCheckInTooltipIsOpen}
            target={"Tooltip-NoCheckIn"}
            toggle={toggleNoCheckInTooltip}
            fade={false}
            autohide={false}
            className="apl-presence__students-tooltip"
          >
            {studentsByStatus.noCheckIn.map(renderStudentLink)}
          </Tooltip>
        </div>

        <div className="apl-presence__legend-card">
          <div className="apl-presence__legend-title">
            <FormattedMessage id="Status" />
          </div>
          <APLPresenceColorInfo />
          <hr className="apl-presence__legend-divider" />
          <div className="apl-presence__datepicker-title">
            <FormattedMessage id="SelectDateToSeeLatestCheckinsForAllStudents" />
          </div>
          <div className="apl-presence__datepicker-hint">
            (<FormattedMessage id="OnlyCheckinsThatHasALocationIsShown" />)
          </div>
          <div className="apl-presence__datepicker-row datepicker-show-map-pins-container">
            <DatePicker
              selected={pendingDate}
              onChange={(value) => setPendingDate(value)}
              locale="sv"
              dateFormat="yyyy-MM-dd"
              className="text-center input date--picker"
              placeholderText="yyyy-MM-dd"
            />
            <Button
              size="sm"
              outline
              color="primary"
              disabled={pendingDate == null}
              onClick={() => setSelectedDateForPosition(pendingDate)}
            >
              <FormattedMessage id="Show" />
            </Button>
          </div>
        </div>
      </div>

      <div className="apl-presence__section-head">
        <h2 className="apl-presence__section-title">
          <FormattedMessage id="StudentStatus" />
          <span className="apl-presence__section-period">
            (
            {format(parseISO(attendanceByWeek.data.periodFrom), "MMM d yyyy", {
              locale: sv,
            })}
            {" - "}
            {format(parseISO(attendanceByWeek.data.periodTo), "MMM d yyyy", {
              locale: sv,
            })}
            )
          </span>
        </h2>
        <Input
          type="search"
          className="apl-presence__search"
          placeholder={intl.formatMessage({ id: "SearchStudent" })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="apl-presence__tabs">
          <button
            type="button"
            className={
              "apl-presence__tab" +
              (activeTab === "all" ? " apl-presence__tab--active" : "")
            }
            onClick={() => setActiveTab("all")}
          >
            <FormattedMessage id="AllStudents" />
          </button>
          <button
            type="button"
            className={
              "apl-presence__tab" +
              (activeTab === "alerts" ? " apl-presence__tab--active" : "")
            }
            onClick={() => setActiveTab("alerts")}
          >
            <FormattedMessage id="NotCheckedIn" /> ({notCheckedInRightNow.size})
          </button>
        </div>
      </div>

      {filteredStudents.map((student) => {
        const todayStatus =
          todayStatusByStudentId.get(student.studentId) || "noCheckIn";
        const { dotModifier, messageId } =
          TODAY_STATUS[todayStatus] ?? TODAY_STATUS.noCheckIn;

        return (
          <Card
            key={student.studentId}
            id={`apl-student-${student.studentId}`}
            className="apl-presence__student-card"
          >
            <div className="apl-presence__student-row">
              <StudentAvatar
                studentId={student.studentId}
                avatar={student.student.avatar}
                name={student.student.name}
              />
              <div className="apl-presence__student-info">
                <div className="apl-presence__student-name">
                  {student.student.name}
                </div>
                <div className="apl-presence__student-today">
                  <span
                    className={
                      "apl-presence__legend-dot apl-presence__legend-dot--" +
                      dotModifier
                    }
                  />
                  <FormattedMessage id={messageId} />
                </div>
                <Button
                  onClick={() =>
                    setStudentForFoodCompensation({
                      name: student.student.name,
                      id: student.studentId,
                    })
                  }
                  size="sm"
                  outline
                  color="primary"
                  className="apl-presence__food-comp-btn"
                >
                  <FormattedMessage id="ShowFoodCompensation" />
                </Button>
              </div>
              <div className="apl-presence__student-weeks">
                <APLPresenceWeeksView
                  attendanceByWeek={attendanceByWeek}
                  studentId={student.studentId}
                  objId={objId}
                  level={level}
                  activePeriodId={activePeriodId}
                />
              </div>
            </div>
          </Card>
        );
      })}
      {studentForFoodCompensation !== null && (
        <FoodCompensationInformationForStudentModal
          close={() => setStudentForFoodCompensation(null)}
          isOpen={studentForFoodCompensation !== null}
          student={studentForFoodCompensation}
          periodId={activePeriodId}
        />
      )}
      {markersWithPosition != null && (
        <CheckInMapForAll
          isOpen={markersWithPosition != null}
          toggle={() => {
            setMarkersWithPosition(null);
            setSelectedDateForPosition(null);
          }}
          markerPoints={markersWithPosition}
        />
      )}
    </div>
  );
}
